/**
 * Host billing: what the host charges each organization for using the CRM.
 *
 *   * NEEDS "MANAGE BILLING". Everything here is refused server-side without it, so the rail hides the
 *     entry and a deep link renders a sentence instead of empty cards.
 *   * FOUR TABS. Invoices (receivables, and every invoice with its actions), Organizations (each
 *     one's plan and what it would come to today), Settings (the monthly run and what invoices carry)
 *     and Accounts (the host's own mail and PayPal - separate from every organization's).
 *   * ONE PLAN EDITOR AND ONE INVOICE PANEL, not one per row: rows are rebuilt on every refresh.
 *   * EVERY ACTION THAT SENDS MAIL OR MOVES MONEY ASKS FIRST, and says who it goes to.
 */
import { TextArea, type Container } from 'webwin';
import * as billing from '../api/hostBilling.js';
import * as hostAccess from '../api/hostAccess.js';
import { describeError } from '../api/http.js';
import {
  HostBillingInterval,
  HostBillingLimits,
  HostInvoiceEmailKind,
  HostPermission,
  InvoicePaymentStatus,
  InvoiceStatus,
  type HostBillingSettingsDto,
  type HostInvoiceDto,
  type HostInvoiceSummaryDto,
  type HostTenantBillingDto
} from '../contracts.gen.js';
import { openInNewTab } from '../download.js';
import { join, localDate, localDateTime, plainDate, plural } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { ask, confirm } from '../ui/dialogs.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { stat } from '../ui/Stat.js';
import { StatusLine } from '../ui/StatusLine.js';
import { TabStrip } from '../ui/TabStrip.js';
import {
  button,
  buttonRow,
  card,
  caption,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

/** An empty half of a field row, so a lone field keeps the width of the others. */
function spacerField() {
  return vstack({ spacing: 0 });
}
import { mailAccountSection } from './sections/mail-account.js';
import { payPalSection } from './sections/paypal.js';

/** Money in the invoice's own currency: "$1,234.00" for dollars, "1,234.00 EUR" otherwise. */
export function hostMoney(amount: number, currency: string): string {
  const zero = currency === 'JPY' || currency === 'HUF' || currency === 'TWD';
  const digits = zero ? 0 : 2;
  const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const sign = amount < 0 ? '-' : '';
  return currency === 'USD' ? `${sign}$${formatted}` : `${sign}${formatted} ${currency}`;
}

const STATUS_FILTERS = [
  { label: 'All invoices', status: null, overdue: false },
  { label: 'Overdue', status: null, overdue: true },
  { label: 'Issued (unpaid)', status: InvoiceStatus.Issued, overdue: false },
  { label: 'Drafts', status: InvoiceStatus.Draft, overdue: false },
  { label: 'Paid', status: InvoiceStatus.Paid, overdue: false },
  { label: 'Void', status: InvoiceStatus.Void, overdue: false }
] as const;

/** In `HostBillingInterval` order. */
const INTERVALS = ['Monthly', 'Annually'] as const;

/** A period end is the first instant after it; the last day of service is the day before, as a calendar date. */
export function lastDay(endUtc: string | null | undefined): string {
  if (!endUtc) return 'today';
  const d = new Date(new Date(endUtc).getTime() - 86_400_000);
  return d.toISOString().slice(0, 10);
}

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'Draft',
  [InvoiceStatus.Issued]: 'Issued',
  [InvoiceStatus.Paid]: 'Paid',
  [InvoiceStatus.Void]: 'Void'
};

const EMAIL_KIND: Record<HostInvoiceEmailKind, string> = {
  [HostInvoiceEmailKind.Invoice]: 'Invoice',
  [HostInvoiceEmailKind.Reminder]: 'Reminder',
  [HostInvoiceEmailKind.Receipt]: 'Receipt'
};

function statusChip(i: HostInvoiceDto) {
  if (i.isOverdue) return chip('Overdue', 'danger');
  return chip(STATUS_LABEL[i.status], i.status === InvoiceStatus.Paid ? 'ok' : i.status === InvoiceStatus.Void ? 'neutral' : 'info');
}

function parseNumber(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (t.length === 0) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseWhole(text: string): number | null {
  const n = parseNumber(text);
  return n !== null && Number.isInteger(n) ? n : null;
}

export function hostBillingPage(): CrmPage {
  return {
    mount(host_: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host_.controls.add(view);

      const status = new StatusLine();
      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Billing'),
        muted('What this server charges each organization for using the CRM: a monthly invoice from each one\'s plan, emailed with a PDF and a PayPal pay link.')
      );
      content.controls.add(header);

      if (hostAccess.isLoaded() && !hostAccess.has(HostPermission.ManageBilling)) {
        const notice = card();
        notice.controls.add(text('Host billing needs the Manage billing permission. Another host operator with Manage operators can grant it.'));
        content.controls.add(notice);
        relayout(content);
        return;
      }

      const runButton = button('Run billing now', () => void runNow(), true);
      header.controls.add(stackOf(buttonRow(), runButton, button('Refresh', () => void loadAll())));
      header.controls.add(status.label);

      const panels = {
        invoices: vstack({ spacing: GAP.lg }),
        plans: vstack({ spacing: GAP.lg }),
        settings: vstack({ spacing: GAP.lg }),
        accounts: vstack({ spacing: GAP.lg })
      };
      const tabHost = vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false });
      const tabs = new TabStrip(tabHost, [
        { key: 'invoices', label: 'Invoices', panel: panels.invoices },
        { key: 'plans', label: 'Organizations', panel: panels.plans },
        { key: 'settings', label: 'Settings', panel: panels.settings },
        { key: 'accounts', label: 'Accounts', panel: panels.accounts }
      ]);
      content.controls.add(tabs.view);
      content.controls.add(tabHost);
      const wanted = ctx.query.get('tab');
      if (wanted && wanted in panels) tabs.select(wanted);
      tabs.onChange(key => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', key);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        relayout(panels[key as keyof typeof panels]);
      });

      let plans: HostTenantBillingDto[] = [];
      let settingsDto: HostBillingSettingsDto | null = null;

      // =================================================================
      //  Invoices
      // =================================================================

      const stats = {
        outstanding: stat('Outstanding'),
        overdue: stat('Overdue'),
        paid: stat('Paid this year'),
        drafts: stat('Drafts')
      };
      const statCard = card();
      statCard.controls.add(stackOf(buttonRow({ spacing: GAP.lg }), stats.outstanding.panel, stats.overdue.panel, stats.paid.panel, stats.drafts.panel));

      const statusFilter = comboBox<string>(STATUS_FILTERS.map(f => f.label), { selectedIndex: 0 });
      const orgFilter = comboBox<string>(['All organizations'], { selectedIndex: 0 });
      statusFilter.on('selectionchanged', () => void loadInvoices());
      orgFilter.on('selectionchanged', () => void loadInvoices());
      const countLine = muted('');
      const invoiceStatus = new StatusLine();

      const detail = card({ visible: false });
      const detailBody = vstack({ spacing: GAP.md });
      detail.controls.add(detailBody);
      let openId: number | null = null;

      const invoiceList = new CardList<HostInvoiceDto>({ key: i => i.id, version: i => i, build: i => invoiceRow(i) });
      const listCard = card();
      listCard.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), fieldRow(field('Show', statusFilter), field('Organization', orgFilter)), countLine, invoiceStatus.label, invoiceList.panel)
      );
      stackOf(panels.invoices, statCard, detail, listCard);

      function filterTenantId(): number | null {
        const index = orgFilter.selectedIndex;
        return index > 0 ? (plans[index - 1]?.tenantId ?? null) : null;
      }

      function bindSummary(s: HostInvoiceSummaryDto, currency: string): void {
        stats.outstanding.set(hostMoney(s.outstandingTotal, currency), plural(s.outstandingCount, 'invoice'));
        stats.overdue.set(hostMoney(s.overdueTotal, currency), plural(s.overdueCount, 'invoice'), s.overdueCount > 0 ? 'danger' : 'text');
        stats.paid.set(hostMoney(s.paidThisYear, currency), 'since January 1');
        stats.drafts.set(String(s.drafts), s.drafts > 0 ? 'waiting to be issued' : 'none waiting');
      }

      async function loadInvoices(): Promise<void> {
        const f = STATUS_FILTERS[statusFilter.selectedIndex] ?? STATUS_FILTERS[0];
        try {
          const list = await billing.invoices({ tenantId: filterTenantId(), status: f.status, overdue: f.overdue }, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          bindSummary(list.summary, settingsDto?.currency ?? 'USD');
          invoiceList.set(list.invoices);
          countLine.text = list.invoices.length === 0 ? 'No invoices match.' : plural(list.invoices.length, 'invoice');
          const open = openId === null ? null : list.invoices.find(i => i.id === openId);
          if (open) showDetail(open);
          relayout(panels.invoices);
        } catch (error) {
          if (!ctx.cancelled()) invoiceStatus.error('Could not load the invoices: ' + describeError(error));
        }
      }

      function invoiceActions(i: HostInvoiceDto, report: StatusLine): Container {
        const row = buttonRow();
        if (i.status === InvoiceStatus.Draft) row.controls.add(button('Issue and email', () => void issue(i, report), true));
        if (i.status === InvoiceStatus.Issued) row.controls.add(button('Mark paid', () => void markPaid(i, report), true));
        if (i.status === InvoiceStatus.Issued || i.status === InvoiceStatus.Paid)
          row.controls.add(button(i.status === InvoiceStatus.Paid ? 'Send receipt' : i.isOverdue ? 'Send reminder' : 'Email again', () => void resend(i, report)));
        row.controls.add(button('PDF', () => void openInNewTab(billing.pdfPath(i.id), `${i.number}.pdf`)));
        if (i.status === InvoiceStatus.Draft || i.status === InvoiceStatus.Issued) row.controls.add(button('Void', () => void voidIt(i, report)));
        return row;
      }

      function invoiceRow(i: HostInvoiceDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(i.number, { font: FONT.bold }));
        heading.controls.add(statusChip(i));
        if (i.interval === HostBillingInterval.Annual) heading.controls.add(chip('annual', 'info'));
        if (i.automatic) heading.controls.add(chip('monthly run', 'neutral'));

        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          text(`${i.tenantName} - ${i.periodLabel}`, { font: FONT.small }),
          muted(join([i.issuedUtc ? `Issued ${plainDate(i.issuedUtc)}` : 'Not issued', i.dueUtc ? `due ${plainDate(i.dueUtc)}` : null, i.paidUtc ? `paid ${plainDate(i.paidUtc)}` : null]))
        );
        const row = splitRow({ align: 'center' });
        fillWith(row, left);
        row.controls.add(value(hostMoney(i.total, i.currency), { font: FONT.bold }));

        const shell = card();
        shell.controls.add(row);
        shell.onActivate(() => showDetail(i));
        return shell;
      }

      function showDetail(i: HostInvoiceDto): void {
        openId = i.id;
        detailBody.controls.clear();
        const report = new StatusLine();
        const heading = inlineRow();
        heading.controls.add(value(`Invoice ${i.number}`, { font: FONT.h2 }));
        heading.controls.add(statusChip(i));
        const top = splitRow({ align: 'start' });
        fillWith(
          top,
          stackOf(
            vstack({ spacing: GAP.xs }),
            heading,
            text(`${i.billToName || i.tenantName} - ${i.billToEmail || 'no billing address'}`),
            muted(join([i.periodLabel, `created ${localDateTime(i.createdUtc)} by ${i.createdBy}`]))
          )
        );
        top.controls.add(
          button('Close', () => {
            openId = null;
            detail.visible = false;
            relayout(panels.invoices);
          })
        );
        detailBody.controls.add(top);

        const lines = vstack({ spacing: GAP.xs });
        for (const l of i.lines) {
          const r = splitRow();
          fillWith(r, text(`${l.description} (${l.quantity} x ${hostMoney(l.unitPrice, i.currency)})`));
          r.controls.add(value(hostMoney(l.amount, i.currency)));
          lines.controls.add(r);
        }
        if (i.taxAmount > 0) {
          const r = splitRow();
          fillWith(r, muted(`Tax (${i.taxPercent}%)`));
          r.controls.add(value(hostMoney(i.taxAmount, i.currency)));
          lines.controls.add(r);
        }
        const totalRow = splitRow();
        fillWith(totalRow, value('Total', { font: FONT.bold }));
        totalRow.controls.add(value(hostMoney(i.total, i.currency), { font: FONT.bold }));
        lines.controls.add(totalRow);
        detailBody.controls.add(lines);

        if (i.paidNote) detailBody.controls.add(muted(i.paidNote, 'ok'));
        if (i.payUrl && i.status === InvoiceStatus.Issued) {
          detailBody.controls.add(stackOf(vstack({ spacing: GAP.xs }), caption('Pay link'), text(i.payUrl, { font: FONT.small })));
        }

        detailBody.controls.add(invoiceActions(i, report));
        detailBody.controls.add(report.label);

        if (i.emails.length > 0) {
          const mails = stackOf(vstack({ spacing: GAP.xs }), caption('Email history'));
          for (const e of i.emails)
            mails.controls.add(
              muted(
                `${localDateTime(e.whenUtc)} - ${EMAIL_KIND[e.kind]} ${e.success ? 'sent to' : 'NOT sent to'} ${e.to || '(nobody)'}${e.success ? '' : ': ' + e.error} (${e.actor})`,
                e.success ? 'muted' : 'danger'
              )
            );
          detailBody.controls.add(mails);
        }
        if (i.payments.length > 0) {
          const pays = stackOf(vstack({ spacing: GAP.xs }), caption('Online payments'));
          for (const p of i.payments)
            pays.controls.add(
              muted(
                join([
                  localDateTime(p.createdUtc),
                  InvoicePaymentStatus[p.status],
                  hostMoney(p.amount, p.currency),
                  p.payerEmail || null,
                  p.sandbox ? 'sandbox' : null,
                  p.error || null
                ]),
                p.status === InvoicePaymentStatus.Completed ? 'ok' : p.status === InvoicePaymentStatus.Failed ? 'danger' : 'muted'
              )
            );
          detailBody.controls.add(pays);
        }
        detail.visible = true;
        relayout(detailBody);
        relayout(panels.invoices);
      }

      async function issue(i: HostInvoiceDto, report: StatusLine): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Issue invoice',
          message: `Issue ${i.number} to ${i.tenantName} for ${hostMoney(i.total, i.currency)} and email it to ${i.billToEmail || '(no address)'}? It becomes payable, and its due date is set from today.`
        });
        if (!ok) return;
        report.busy('Issuing…');
        try {
          const r = await billing.issue(i.id, true);
          if (ctx.cancelled()) return;
          showDetail(r.invoice);
          await loadInvoices();
          if (r.warning) invoiceStatus.error(`Issued ${i.number}, but it was not emailed: ${r.warning}`);
          else invoiceStatus.ok(`Issued ${i.number} and emailed it to ${r.invoice.billToEmail}.`);
        } catch (error) {
          if (!ctx.cancelled()) report.error(describeError(error));
        }
      }

      async function resend(i: HostInvoiceDto, report: StatusLine): Promise<void> {
        const what = i.status === InvoiceStatus.Paid ? 'a receipt for' : i.isOverdue ? 'a reminder about' : 'a copy of';
        const ok = await confirm(ctx.desktop, { title: 'Email invoice', message: `Email ${what} ${i.number} to ${i.billToEmail}?` });
        if (!ok) return;
        report.busy('Sending…');
        try {
          const r = await billing.send(i.id);
          if (ctx.cancelled()) return;
          showDetail(r.invoice);
          await loadInvoices();
          if (r.success) invoiceStatus.ok(r.message);
          else invoiceStatus.error(`Not sent: ${r.message}`);
        } catch (error) {
          if (!ctx.cancelled()) report.error(describeError(error));
        }
      }

      async function markPaid(i: HostInvoiceDto, report: StatusLine): Promise<void> {
        const note = await ask(ctx.desktop, {
          title: `Mark ${i.number} paid`,
          prompt: `How was it paid? (optional - a receipt is emailed)`,
          okLabel: 'Mark paid',
          width: 480
        });
        if (note === null) return;
        report.busy('Saving…');
        try {
          const paid = await billing.markPaid(i.id, note.trim() || null, true);
          if (ctx.cancelled()) return;
          showDetail(paid);
          await loadInvoices();
          await loadPlans();
          invoiceStatus.ok(`${i.number} is paid.`);
        } catch (error) {
          if (!ctx.cancelled()) report.error(describeError(error));
        }
      }

      async function voidIt(i: HostInvoiceDto, report: StatusLine): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Void invoice',
          message: `Void ${i.number} to ${i.tenantName}? It can no longer be paid, and ${i.periodLabel} can then be billed again. Nothing is emailed.`
        });
        if (!ok) return;
        try {
          const voided = await billing.voidInvoice(i.id);
          if (ctx.cancelled()) return;
          showDetail(voided);
          await loadInvoices();
          await loadPlans();
          invoiceStatus.ok(`${i.number} is void.`);
        } catch (error) {
          if (!ctx.cancelled()) report.error(describeError(error));
        }
      }

      // =================================================================
      //  Organizations
      // =================================================================

      const planIntro = muted(
        'On the billing day each organization is invoiced for its next period - a month, or a year for an annual plan, in its anniversary month: the plan\'s fee plus each managed device and enabled staff account above what the plan includes, counted at that moment. Nothing is billed until a plan is switched on. A terminated plan bills nothing more, and its organization keeps service to the end of the last period invoiced, then is suspended.'
      );
      const planStatus = new StatusLine();

      let editingPlan: HostTenantBillingDto | null = null;
      const planTitle = h2('Plan');
      const planEnabled = wrappingCheck('Bill this organization');
      const planInterval = comboBox<string>(INTERVALS, { selectedIndex: HostBillingInterval.Monthly });
      const priceNote = muted('');
      planInterval.on('selectionchanged', () => showPriceNote());
      const planName = textBox({ placeholder: 'Standard' });
      const baseFee = moneyBox();
      const perDevice = moneyBox();
      const includedDevices = textBox({ text: '0' });
      const perStaff = moneyBox();
      const includedStaff = textBox({ text: '0' });
      const planTax = textBox({ placeholder: 'Blank uses the default rate' });
      const billName = textBox();
      const billEmail = textBox({ placeholder: 'accounts@theirbusiness.com' });
      const billAddress = new TextArea({ height: 80, hAlign: 'stretch', wrap: true });
      const planNotes = textBox();
      const estimateLine = muted('');
      const planEditorStatus = new StatusLine();
      const planEditor = card({ visible: false });
      planEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          planTitle,
          checkLine(planEnabled),
          fieldRow(field('Plan name', planName), field('Billed', planInterval)),
          priceNote,
          fieldRow(field('Plan fee', baseFee.row), spacerField()),
          fieldRow(field('Per managed device', perDevice.row), field('Devices included', includedDevices)),
          fieldRow(field('Per staff account', perStaff.row), field('Staff accounts included', includedStaff)),
          field('Tax rate % (optional)', planTax),
          estimateLine,
          caption('Invoices go to'),
          fieldRow(field('Name on the invoice', billName), field('Email (comma-separate several)', billEmail)),
          field('Billing address', billAddress),
          field('Notes (not on the invoice)', planNotes),
          stackOf(buttonRow(), button('Save plan', () => void savePlan(), true), button('Cancel', () => closePlan())),
          planEditorStatus.label
        )
      );

      const planList = new CardList<HostTenantBillingDto>({ key: p => p.tenantId, version: p => p, build: p => planRow(p) });
      const planListCard = card();
      planListCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), planIntro, planStatus.label, planList.panel));
      stackOf(panels.plans, planEditor, planListCard);

      function money(p: HostTenantBillingDto, n: number): string {
        return hostMoney(n, settingsDto?.currency ?? 'USD');
      }

      function showPriceNote(): void {
        priceNote.text =
          planInterval.selectedIndex === HostBillingInterval.Annual
            ? 'Every price here is per year. The plan is invoiced once a year, in the month its current year starts.'
            : 'Every price here is per month.';
      }

      function planSummary(p: HostTenantBillingDto): string {
        const per = p.interval === HostBillingInterval.Annual ? 'a year' : 'a month';
        const parts = [`${p.planName || 'Standard'}: ${money(p, p.baseMonthly)} ${per}`];
        if (p.perDevice > 0) parts.push(`${money(p, p.perDevice)} per device over ${p.includedDevices}`);
        if (p.perStaff > 0) parts.push(`${money(p, p.perStaff)} per staff account over ${p.includedStaff}`);
        return parts.join(', ');
      }

      function planRow(p: HostTenantBillingDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(p.tenantName, { font: FONT.bold }));
        const annual = p.interval === HostBillingInterval.Annual;
        if (p.inTrial) heading.controls.add(chip(`Free trial - last day ${lastDay(p.trialEndsUtc)}`, 'info'));
        if (p.trialExpired) heading.controls.add(chip('Trial ended - suspended', 'danger'));
        if (p.serviceEnded) heading.controls.add(chip('Service ended', 'danger'));
        else if (p.terminatedUtc) heading.controls.add(chip(`Terminated - service ends ${lastDay(p.serviceEndsUtc)}`, 'warn'));
        else heading.controls.add(p.enabled ? chip(annual ? 'Billed annually' : 'Billed monthly', 'ok') : chip('Not billed', 'neutral'));
        if (p.overdueInvoices > 0) heading.controls.add(chip(plural(p.overdueInvoices, 'overdue invoice'), 'danger'));

        const usage =
          p.managedDevices === null
            ? 'Not running - usage cannot be measured'
            : `${plural(p.managedDevices, 'managed device')}, ${plural(p.staffAccounts ?? 0, 'staff account')} now`;
        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          text(planSummary(p), { font: FONT.small }),
          muted(join([usage, p.estimatedMonthly !== null ? `would bill ${money(p, p.estimatedMonthly)} before tax` : null])),
          muted(join([p.billingEmail ? `Invoices to ${p.billingEmail}` : 'No billing address', p.outstandingTotal > 0 ? `${money(p, p.outstandingTotal)} outstanding` : null])),
          muted(
            join([
              p.billedThroughUtc ? `Invoiced through ${lastDay(p.billedThroughUtc)}` : 'Never invoiced',
              p.paidThroughUtc ? `paid through ${lastDay(p.paidThroughUtc)}` : null,
              p.nextPeriodLabel && p.enabled ? `next invoice: ${p.nextPeriodLabel}` : null
            ])
          )
        );
        if (p.inTrial)
          body.controls.add(
            muted(
              `On a free trial since ${localDate(p.trialStartedUtc)}: nothing is invoiced through ${lastDay(p.trialEndsUtc)}. ` +
                (p.enabled
                  ? 'Then the plan begins and its first invoice is sent.'
                  : 'The plan is not switched on, so the organization will be suspended when the trial ends unless it is.'),
              p.enabled ? 'muted' : 'warn'
            )
          );
        else if (p.trialExpired)
          body.controls.add(
            muted('Its free trial ended with the plan switched off, so it was suspended. Switching the plan on resumes it and raises its first invoice.', 'danger')
          );
        if (p.terminatedUtc)
          body.controls.add(
            muted(
              `Terminated ${localDate(p.terminatedUtc)}${p.terminatedBy ? ' by ' + p.terminatedBy : ''}. ` +
                (p.serviceEnded
                  ? `Service ended ${lastDay(p.serviceEndsUtc)}.`
                  : `Nothing more is invoiced; service continues through ${lastDay(p.serviceEndsUtc)}, then the organization is suspended.`),
              p.serviceEnded ? 'danger' : 'warn'
            )
          );
        const actions = buttonRow();
        actions.controls.add(button('Edit plan', () => openPlan(p)));
        if (!p.terminatedUtc && !p.inTrial) actions.controls.add(button(annual ? 'Bill now' : 'Bill this month now', () => void billNow(p)));
        if (p.inTrial) actions.controls.add(button('End trial now', () => void endTrialNow(p)));
        else if (!p.trialStartedUtc && !p.billedThroughUtc && !p.terminatedUtc) actions.controls.add(button('Start free trial', () => void startTrial(p)));
        actions.controls.add(button('Invoices', () => showInvoicesFor(p.tenantId)));
        if (p.terminatedUtc) actions.controls.add(button('Reinstate plan', () => void reinstate(p)));
        else if (p.billedThroughUtc || p.enabled) actions.controls.add(button('Terminate plan', () => void terminate(p)));
        body.controls.add(actions);
        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function showInvoicesFor(tenantId: number): void {
        const index = plans.findIndex(p => p.tenantId === tenantId);
        orgFilter.selectedIndex = index + 1;
        tabs.select('invoices');
        void loadInvoices();
      }

      function openPlan(p: HostTenantBillingDto): void {
        editingPlan = p;
        planTitle.text = `Plan for ${p.tenantName}`;
        planEnabled.checked = p.enabled;
        planInterval.selectedIndex = p.interval;
        showPriceNote();
        planName.text = p.planName;
        baseFee.set(p.baseMonthly);
        perDevice.set(p.perDevice);
        includedDevices.text = String(p.includedDevices);
        perStaff.set(p.perStaff);
        includedStaff.text = String(p.includedStaff);
        planTax.text = p.taxPercent === null ? '' : String(p.taxPercent);
        billName.text = p.billingName;
        billEmail.text = p.billingEmail;
        billAddress.text = p.billingAddress;
        planNotes.text = p.notes;
        estimateLine.text =
          p.managedDevices === null
            ? 'This organization is not running, so its usage cannot be read.'
            : `Today: ${plural(p.managedDevices, 'managed device')} and ${plural(p.staffAccounts ?? 0, 'staff account')}${p.estimatedMonthly !== null ? `, which the saved plan bills as ${money(p, p.estimatedMonthly)} before tax` : ''}.`;
        planEditorStatus.set('');
        planStatus.set('');
        planEditor.visible = true;
        relayout(panels.plans);
      }

      function closePlan(): void {
        editingPlan = null;
        planEditor.visible = false;
        relayout(panels.plans);
      }

      async function savePlan(): Promise<void> {
        if (!editingPlan) return;
        const devices = parseWhole(includedDevices.text);
        const staff = parseWhole(includedStaff.text);
        const tax = planTax.text.trim().length === 0 ? null : parseNumber(planTax.text);
        if (devices === null || staff === null || devices < 0 || staff < 0) {
          planEditorStatus.error('The included counts must be whole numbers, 0 or more.');
          return;
        }
        if (planTax.text.trim().length > 0 && tax === null) {
          planEditorStatus.error('The tax rate must be a number, or blank for the default.');
          return;
        }
        for (const [box, name] of [[baseFee, 'plan fee'], [perDevice, 'price per device'], [perStaff, 'price per staff account']] as const) {
          if (box.text().trim().length > 0 && box.value() === null) {
            planEditorStatus.error(`The ${name} must be an amount.`);
            return;
          }
        }
        planEditorStatus.busy('Saving…');
        try {
          const { plan: saved, warning } = await billing.updatePlan(editingPlan.tenantId, {
            enabled: planEnabled.checked,
            interval: planInterval.selectedIndex as HostBillingInterval,
            planName: planName.text.trim(),
            baseMonthly: baseFee.value() ?? 0,
            perDevice: perDevice.value() ?? 0,
            includedDevices: devices,
            perStaff: perStaff.value() ?? 0,
            includedStaff: staff,
            taxPercent: tax,
            clearTaxPercent: tax === null,
            billingName: billName.text.trim(),
            billingEmail: billEmail.text.trim(),
            billingAddress: billAddress.text.trim(),
            notes: planNotes.text.trim()
          });
          if (ctx.cancelled()) return;
          closePlan();
          await loadPlans();
          if (warning) planStatus.error(warning);
          else planStatus.ok(`Saved the plan for ${saved.tenantName}.`);
        } catch (error) {
          if (!ctx.cancelled()) planEditorStatus.error(describeError(error));
        }
      }

      async function billNow(p: HostTenantBillingDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Bill now',
          message: `Raise ${p.tenantName}'s invoice for ${p.nextPeriodLabel ?? 'its next period'} now, at today's usage${p.estimatedMonthly !== null ? ` (${money(p, p.estimatedMonthly)} before tax)` : ''}, issue it and email it to ${p.billingEmail || '(no address)'}? The monthly run will not bill that period again.`
        });
        if (!ok) return;
        planStatus.busy(`Billing ${p.tenantName}…`);
        try {
          const r = await billing.billNow(p.tenantId, true);
          if (ctx.cancelled()) return;
          await loadPlans();
          await loadInvoices();
          if (r.warning) planStatus.error(`Raised ${r.invoice.number} for ${hostMoney(r.invoice.total, r.invoice.currency)}, but it was not emailed: ${r.warning}`);
          else planStatus.ok(`Raised ${r.invoice.number} for ${hostMoney(r.invoice.total, r.invoice.currency)} and emailed it to ${r.invoice.billToEmail}.`);
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error(describeError(error));
        }
      }

      async function startTrial(p: HostTenantBillingDto): Promise<void> {
        const days = settingsDto?.trialDays ?? HostBillingLimits.DefaultTrialDays;
        const ok = await confirm(ctx.desktop, {
          title: 'Start free trial',
          message: `Start a ${days}-day free trial for ${p.tenantName}? Nothing is invoiced until it ends, and ${p.billingEmail || 'its billing address'} is told now and reminded a week before. When it ends, ${p.enabled ? 'its plan begins and the first invoice is sent' : 'the organization is suspended unless its plan has been switched on'}. An organization gets one trial.`
        });
        if (!ok) return;
        try {
          const started = await billing.startTrial(p.tenantId, null, true);
          if (ctx.cancelled()) return;
          await loadPlans();
          planStatus.ok(`${p.tenantName} is on a free trial through ${lastDay(started.trialEndsUtc)}.`);
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error(describeError(error));
        }
      }

      async function endTrialNow(p: HostTenantBillingDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'End trial now',
          message: p.enabled
            ? `End ${p.tenantName}'s free trial now? Its plan begins today and its first invoice is raised and emailed to ${p.billingEmail}.`
            : `End ${p.tenantName}'s free trial now? Its plan is not switched on, so the organization is suspended at once - its people and agents are refused until the plan is switched on.`
        });
        if (!ok) return;
        try {
          await billing.endTrial(p.tenantId);
          if (ctx.cancelled()) return;
          await loadPlans();
          await loadInvoices();
          planStatus.ok(`${p.tenantName}'s trial has ended.`);
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error(describeError(error));
        }
      }

      async function terminate(p: HostTenantBillingDto): Promise<void> {
        const reason = await ask(ctx.desktop, {
          title: `Terminate ${p.tenantName}'s plan`,
          prompt: 'Reason (optional - kept on the plan and quoted in the suspension)',
          okLabel: 'Continue',
          width: 520
        });
        if (reason === null) return;
        const ends = p.billedThroughUtc && new Date(p.billedThroughUtc).getTime() > Date.now() ? lastDay(p.billedThroughUtc) : 'today';
        const ok = await confirm(ctx.desktop, {
          title: 'Terminate plan',
          message: `Terminate ${p.tenantName}'s plan? Nothing more is invoiced. Its service continues through ${ends}, the end of the last period invoiced - then the organization is suspended and its people and agents are refused. ${p.billingEmail ? `${p.billingEmail} is emailed the date.` : ''} You can reinstate the plan before or after that day.`
        });
        if (!ok) return;
        planStatus.busy(`Terminating ${p.tenantName}'s plan…`);
        try {
          const done = await billing.terminate(p.tenantId, reason.trim() || null, true);
          if (ctx.cancelled()) return;
          await loadPlans();
          planStatus.ok(
            done.serviceEnded
              ? `${p.tenantName}'s plan is terminated and its service has ended.`
              : `${p.tenantName}'s plan is terminated; its service continues through ${lastDay(done.serviceEndsUtc)}.`
          );
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error(describeError(error));
        }
      }

      async function reinstate(p: HostTenantBillingDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Reinstate plan',
          message: p.serviceEnded
            ? `Reinstate ${p.tenantName}'s plan? The organization is resumed, and its plan bills again from its next period.`
            : `Reinstate ${p.tenantName}'s plan? Its service no longer ends on ${lastDay(p.serviceEndsUtc)}, and it is billed again from its next period.`
        });
        if (!ok) return;
        try {
          await billing.reinstate(p.tenantId);
          if (ctx.cancelled()) return;
          await loadPlans();
          planStatus.ok(`${p.tenantName}'s plan is reinstated.`);
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error(describeError(error));
        }
      }

      async function loadPlans(): Promise<void> {
        try {
          plans = await billing.plans(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          const keep = filterTenantId();
          orgFilter.items = ['All organizations', ...plans.map(p => p.tenantName)];
          const index = keep === null ? -1 : plans.findIndex(p => p.tenantId === keep);
          orgFilter.selectedIndex = index + 1;
          planList.set(plans);
          relayout(panels.plans);
        } catch (error) {
          if (!ctx.cancelled()) planStatus.error('Could not load the organizations: ' + describeError(error));
        }
      }

      // =================================================================
      //  Settings
      // =================================================================

      const sEnabled = wrappingCheck('Raise and email every organization\'s invoice automatically on the billing day');
      const sDay = textBox({ text: '1' });
      const sDue = textBox({ text: '14' });
      const sTax = textBox({ text: '0' });
      const sReminders = textBox({ text: '7' });
      const sPrefix = textBox({ text: 'H-' });
      const sIssuer = textBox({ placeholder: 'Your business name' });
      const sIssuerEmail = textBox({ placeholder: 'billing@yourbusiness.com' });
      const sAddress = new TextArea({ height: 80, hAlign: 'stretch', wrap: true });
      const sFooter = textBox({ placeholder: 'Printed at the bottom of every invoice' });
      const sTrialDays = textBox({ text: String(HostBillingLimits.DefaultTrialDays) });
      const sTrialForNew = wrappingCheck('Start every new organization on a free trial');
      const runLine = muted('');
      const lastRunLine = muted('');
      const currencyLine = muted('');
      const settingsStatus = new StatusLine();
      const settingsCard = card();
      settingsCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Monthly billing'),
          checkLine(sEnabled),
          runLine,
          lastRunLine,
          fieldRow(field('Billing day (1-28)', sDay), field('Payment due after (days)', sDue)),
          fieldRow(field('Default tax rate %', sTax), field('Remind every (days, 0 = never)', sReminders)),
          muted(`An overdue invoice gets a reminder every so many days, at most ${HostBillingLimits.MaxReminders} times. The billing operators are emailed each run's result, and each payment.`),
          h2('On every invoice'),
          fieldRow(field('Issued by', sIssuer), field('Invoice number prefix', sPrefix)),
          field('Contact email', sIssuerEmail),
          field('Address', sAddress),
          field('Footer note', sFooter),
          h2('Free trials'),
          fieldRow(field('Trial length (days)', sTrialDays), spacerField()),
          checkLine(sTrialForNew),
          muted(
            'Nothing is invoiced during a trial, and the organization is reminded a week before it ends. When it ends, a plan that is switched on begins and is invoiced at once; otherwise the organization is suspended until its plan is switched on. A new organization on a trial is suspended at the end unless its plan has been set up by then.'
          ),
          currencyLine,
          stackOf(buttonRow(), button('Save billing settings', () => void saveSettings(), true)),
          settingsStatus.label
        )
      );
      panels.settings.controls.add(settingsCard);

      function bindSettings(s: HostBillingSettingsDto): void {
        settingsDto = s;
        sEnabled.checked = s.enabled;
        sDay.text = String(s.billingDay);
        sDue.text = String(s.dueDays);
        sTax.text = String(s.taxPercent);
        sReminders.text = String(s.reminderDays);
        sPrefix.text = s.invoicePrefix;
        sIssuer.text = s.issuerName;
        sIssuerEmail.text = s.issuerEmail;
        sAddress.text = s.issuerAddress;
        sFooter.text = s.footerNote;
        sTrialDays.text = String(s.trialDays);
        sTrialForNew.checked = s.trialForNewOrganizations;
        runLine.text = s.enabled
          ? s.nextRunUtc
            ? `Next run: ${localDateTime(s.nextRunUtc)}.`
            : ''
          : 'Automatic billing is off. Run billing now and Bill this month now still work.';
        lastRunLine.text = s.lastRunUtc ? `Last run ${localDateTime(s.lastRunUtc)}: ${s.lastRunSummary ?? ''}` : 'Billing has not run yet.';
        currencyLine.text = `Invoices are raised in ${s.currency}, the currency of the host's PayPal account (Accounts tab).`;
        relayout(settingsCard);
      }

      async function saveSettings(): Promise<void> {
        const day = parseWhole(sDay.text);
        const due = parseWhole(sDue.text);
        const reminders = parseWhole(sReminders.text);
        const tax = parseNumber(sTax.text);
        const trialDays = parseWhole(sTrialDays.text);
        if (trialDays === null || trialDays < 1) {
          settingsStatus.error('The trial length must be a whole number of days.');
          return;
        }
        if (day === null || due === null || reminders === null || tax === null) {
          settingsStatus.error('The billing day, terms and reminders must be whole numbers, and the tax rate a number.');
          return;
        }
        settingsStatus.busy('Saving…');
        try {
          const saved = await billing.updateSettings({
            enabled: sEnabled.checked,
            billingDay: day,
            dueDays: due,
            taxPercent: tax,
            reminderDays: reminders,
            invoicePrefix: sPrefix.text.trim(),
            issuerName: sIssuer.text.trim(),
            issuerAddress: sAddress.text.trim(),
            issuerEmail: sIssuerEmail.text.trim(),
            footerNote: sFooter.text.trim(),
            trialDays,
            trialForNewOrganizations: sTrialForNew.checked
          });
          if (ctx.cancelled()) return;
          bindSettings(saved);
          settingsStatus.ok('Billing settings saved.');
        } catch (error) {
          if (!ctx.cancelled()) settingsStatus.error(describeError(error));
        }
      }

      async function loadSettings(): Promise<void> {
        try {
          const s = await billing.settings(ctx.dispose.signal());
          if (!ctx.cancelled()) bindSettings(s);
        } catch (error) {
          if (!ctx.cancelled()) settingsStatus.error('Could not load the billing settings: ' + describeError(error));
        }
      }

      // =================================================================
      //  Accounts
      // =================================================================

      const mail = mailAccountSection(ctx, true, {
        api: billing.mailApi,
        sendOnly: true,
        title: 'Billing mail account',
        description:
          'The mailbox host invoices, reminders, receipts and billing notices are sent from - the host\'s own, separate from every organization\'s. Only sending is used. Passwords are stored encrypted and are never sent back to this page.'
      });
      const paypal = payPalSection(ctx, true, {
        api: billing.payPalApi,
        payer: 'Organizations',
        enableLabel: 'Let organizations pay host invoices with PayPal',
        description:
          'The host\'s own PayPal app, separate from the ones organizations take their customers\' payments through. Every issued host invoice carries a pay link; the invoice is marked paid and a receipt emailed the moment the payment completes. The currency here is the currency new invoices are raised in.'
      });
      stackOf(panels.accounts, mail.card, paypal.card);

      // =================================================================

      async function runNow(): Promise<void> {
        const enabledPlans = plans.filter(p => p.enabled);
        const ok = await confirm(ctx.desktop, {
          title: 'Run billing now',
          message: `Raise and email this month's invoice for every organization that is billed monthly and has not been billed this month (${plural(enabledPlans.length, 'organization')} switched on)?`
        });
        if (!ok) return;
        runButton.enabled = false;
        status.busy('Billing…');
        try {
          const r = await billing.run();
          if (ctx.cancelled()) return;
          await loadAll();
          const line = r.raised === 0 && r.skipped === 0 ? 'Nothing to bill - every organization switched on is already billed this month.' : `${plural(r.raised, 'invoice')} raised, ${r.emailed} emailed${r.emailFailed > 0 ? `, ${r.emailFailed} not emailed` : ''}${r.skipped > 0 ? `, ${r.skipped} not billed` : ''}.`;
          if (r.emailFailed > 0 || r.skipped > 0) status.error(line + ' ' + r.lines.filter(l => /NOT|not billed/.test(l)).join(' '));
          else status.ok(line);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          runButton.enabled = true;
        }
      }

      async function loadAll(): Promise<void> {
        await loadSettings();
        await loadPlans();
        await loadInvoices();
      }

      relayout(content);
      void loadAll();
      void mail.load();
      void paypal.load();
    }
  };
}
